/**
 * Company: SPHMedia
 * Description: Full display of social media links
 */

import {
  ForwardedRef,
  forwardRef,
  ReactElement,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from "react-share";
import {
  faFacebookF,
  faLinkedinIn,
  faWhatsapp,
  faXTwitter,
} from "@fortawesome/free-brands-svg-icons";
import { faEnvelope } from "@fortawesome/free-regular-svg-icons";
import { faCircle, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ENVIRONMENT } from "@util/constant";
import { getEnvironmentDomain } from "@util/helpers";
import cx from "classnames";

type SocialMediaLinksPopoverProps = {
  rootClassName?: string;
};

/**
 * SocialMediaLinksPopover
 * Description: Sub component of the social links in the header.
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
const SocialMediaLinksPopover = (
  { rootClassName = "" }: SocialMediaLinksPopoverProps,
  ref: ForwardedRef<HTMLDivElement>
): ReactElement => {
  const [currentTitle, setCurrentTitle] = useState("");
  const { pathname } = useLocation();
  const currentUrl = `${getEnvironmentDomain(ENVIRONMENT)}${pathname}`;
  // Add component logic and state here
  const socialButtonClass =
    "flex flex-row items-center gap-2 transition duration-300 hover:opacity-50";
  const socialTextClass =
    "inline-block align-middle font-poppins capitalize text-black";
  const iconWrapper = "fa-layers fa-xl h-8 w-8 text-center";
  const socialMediaObjects = [
    {
      media: "facebook",
      icon: faFacebookF,
      SharingButton: FacebookShareButton,
    },
    {
      media: "twitter",
      icon: faXTwitter,
      SharingButton: TwitterShareButton,
    },
    {
      media: "whatsapp",
      icon: faWhatsapp,
      SharingButton: WhatsappShareButton,
    },
    {
      media: "linkedIn",
      icon: faLinkedinIn,
      SharingButton: LinkedinShareButton,
    },
    {
      media: "telegram",
      icon: faPaperPlane,
      SharingButton: TelegramShareButton,
    },
    {
      media: "mail",
      icon: faEnvelope,
      SharingButton: EmailShareButton,
      subject: "Sharing from The Business Times",
      body: currentTitle,
      separator: " - ",
    },
  ];

  useEffect(() => {
    if (typeof document === "undefined") return;

    const currentTitle = document.title;

    setCurrentTitle(currentTitle);
  }, [currentTitle]);

  return (
    <div
      className={cx(
        "grid grid-cols-2 gap-2 gap-x-8 rounded border border-gray-275 bg-gray-150 px-4 py-3",
        rootClassName
      )}
      ref={ref}
    >
      {socialMediaObjects.map(
        ({ media, icon, SharingButton, subject, body, separator }) => (
          <SharingButton
            key={media}
            {...{
              className: socialButtonClass,
              url: currentUrl,
              subject,
              body,
              separator,
              title: currentTitle,
            }}
          >
            <span className={iconWrapper}>
              <FontAwesomeIcon icon={faCircle} size="lg" color="black" />

              <FontAwesomeIcon
                icon={icon}
                transform={{ size: 10 }}
                color="white"
              />
            </span>

            <span className={socialTextClass}>{media}</span>
          </SharingButton>
        )
      )}
    </div>
  );
};

export default forwardRef(SocialMediaLinksPopover);
